import type { Theme } from 'app/providers/theme';
import type React from 'react';
import styled, { useTheme } from 'styled-components';
import Typography from 'app/ui/components/atoms/typography';

export interface Props {
  state: boolean;
  onChange?: (state: boolean) => void;
  disabled?: boolean;
  className?: string;
  label?: string;
  'data-testid'?: string;
}

interface ToggleContainerProps {
  theme: Theme;
  $state: boolean;
  $disabled?: boolean;
}

interface ToggleTrackProps {
  theme: Theme;
  $state: boolean;
  $disabled?: boolean;
}

interface ToggleThumbProps {
  theme: Theme;
  $state: boolean;
  $disabled?: boolean;
}

const ToggleContainer = styled.div<ToggleContainerProps>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(4)};
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
  transition: opacity 0.2s ease;

  &:hover {
    opacity: ${({ $disabled }) => ($disabled ? 0.5 : 0.8)};
  }
`;

const ToggleLabel = styled(Typography)`
  color: ${({ theme }) => theme.colors.focoltone};
  font-weight: ${({ theme }) => theme.typography.bold};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  text-shadow: 0 0 8px ${({ theme }) => theme.colors.focoltone}40;
`;

const ToggleTrack = styled.div<ToggleTrackProps>`
  position: relative;
  width: 3rem;
  height: 1.5rem;
  background: ${({ theme, $state }) => ($state ? theme.colors.focoltone : theme.colors.blackSwan)};
  border-radius: 1rem;
  transition: background-color 0.3s ease;
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const ToggleThumb = styled.div<ToggleThumbProps>`
  position: absolute;
  top: 0.125rem;
  left: ${({ $state }) => ($state ? '1.625rem' : '0.125rem')};
  width: 1.25rem;
  height: 1.25rem;
  background: ${({ theme, $state }) => ($state ? theme.colors.caparol : theme.colors.white)};
  border-radius: 50%;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  transform: ${({ $state }) => ($state ? 'scale(1.1)' : 'scale(1)')};
`;

const Toggle: React.FC<Props> = ({
  state,
  onChange,
  disabled = false,
  className,
  label,
  'data-testid': dataTestId,
}) => {
  const theme = useTheme();

  const handleToggle = () => {
    if (disabled || !onChange) {
      return;
    }

    const newState = !state;
    onChange(newState);
  };

  return (
    <ToggleContainer
      $state={state}
      $disabled={disabled}
      onClick={handleToggle}
      className={className}
      data-testid={dataTestId}
    >
      {label && (
        <ToggleLabel variant="body2" theme={theme}>
          {state ? 'ON' : 'OFF'}
        </ToggleLabel>
      )}
      <ToggleTrack $state={state} $disabled={disabled} theme={theme}>
        <ToggleThumb $state={state} $disabled={disabled} theme={theme} />
      </ToggleTrack>
    </ToggleContainer>
  );
};

export default Toggle;
